import React from 'react';
import Localize from 'react-intl-universal';

import { Field, useFormikContext } from 'formik';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import { ADDRESS_OPTIONS } from '@common/Constants';
import { BOOKING_PROCESS } from '@common/network';
import AutocompleteComponent from '@components/AutocompleteComponent';
import RadioGroupField from '@components/RadioGroup/RadioGroupField';
import SelectInput from '@components/SelectInput/SelectInput';
import TextInput from '@components/TextInput/TextInput';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { AdditionalAddressTypes } from '@pages/BookingProcess/Constants';

const StyledGrid = styled(Grid)(() => ({
  '&.MuiGrid-root': {
    marginTop: '0.1rem'
  }
}));

const StyledHeader = styled(Box)(() => ({
  marginTop: '0.5rem',
  paddingTop: 0,
  fontSize: '20px'
}));

const additionalAddressOptions = (type) => {
  switch (type) {
    case AdditionalAddressTypes.Billing.name:
      return [
        {
          value: ADDRESS_OPTIONS.noAlternative,
          label: Localize.get('Booking.useBillingAddress')
        },
        {
          value: ADDRESS_OPTIONS.alternative,
          label: Localize.get('Booking.useAlternativeBilling')
        }
      ];
    case AdditionalAddressTypes.Communication.name:
      return [
        {
          value: ADDRESS_OPTIONS.noAlternative,
          label: Localize.get('Booking.communicationAddress')
        },
        {
          value: ADDRESS_OPTIONS.alternative,
          label: Localize.get('Booking.alternativeCommunicationAddress')
        }
      ];
    default:
      return [];
  }
};

const AdditionalAddress = ({ values = {}, addressConfig, isEventFree }) => {
  const { setFieldValue, setValues, setFieldTouched } = useFormikContext();
  const {
    name: addressName,
    radioName: addressRadioName,
    title: addressTitle,
    subTitle: addressSubtitle
  } = addressConfig;

  const [contextData] = useGlobalContext();
  const {
    country: countryOptions,
    title: titleOptions,
    academicTitle: academicOptions
  } = contextData?.bookingFormData || {};

  return (
    <>
      <StyledGrid container rowSpacing={{ xs: 2 }} columnSpacing={{ xs: 4, md: 16 }}>
        <Grid item xs={12} sm={12}>
          <StyledHeader>{Localize.get(addressTitle || '')}</StyledHeader>
        </Grid>
        <Grid item xs={12} sm={12}>
          <FormControl size='small' sx={{ alignItems: null }}>
            <Field
              name={addressRadioName}
              component={RadioGroupField}
              options={additionalAddressOptions(addressName)}
            />
          </FormControl>
        </Grid>
        {values?.[addressRadioName] === ADDRESS_OPTIONS.alternative && (
          <>
            <Grid item xs={12} sm={12} sx={{ mb: '0.8rem !important' }}>
              <StyledHeader>{Localize.get(addressSubtitle)}</StyledHeader>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.title`}
                  label={Localize.get('Booking.title')}
                  component={SelectInput}
                  options={titleOptions}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.academicTitle`}
                  label={Localize.get('Booking.academicTitle')}
                  component={SelectInput}
                  options={academicOptions}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.company`}
                  label={Localize.get('Booking.company')}
                  component={TextInput}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.firstName`}
                  label={Localize.get('SignIn.firstName')}
                  component={TextInput}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.lastName`}
                  label={Localize.get('SignIn.lastName')}
                  component={TextInput}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.email`}
                  label={Localize.get('Booking.email')}
                  component={TextInput}
                  required
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.phone`}
                  label={Localize.get('Booking.phone')}
                  component={TextInput}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.mobile`}
                  label={Localize.get('Booking.mobile')}
                  component={TextInput}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.street`}
                  label={Localize.get('Booking.street')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.number`}
                  label={Localize.get('Booking.streetNumber')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.postCode`}
                  label={Localize.get('SignIn.postcode')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl required fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.city`}
                  label={Localize.get('SignIn.city')}
                  component={TextInput}
                  required={!isEventFree}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.country`}
                  label={Localize.get('SignIn.country')}
                  component={AutocompleteComponent}
                  required={!isEventFree}
                  options={countryOptions}
                  // isSelectWithFlag={true}
                  formItemChangeHandler={(_, data) => {
                    setValues({
                      ...values,
                      [addressName]: {
                        ...values[addressName],
                        country: data?.newValue,
                        region: null
                      }
                    });
                    setFieldTouched && setFieldTouched('generalInformation.vatNo', true);
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 1 }}>
              <FormControl fullWidth size='small' sx={{ alignItems: null }}>
                <Field
                  name={`${addressName}.region`}
                  label={Localize.get('Booking.region')}
                  component={AutocompleteComponent}
                  formItemChangeHandler={(_, data) =>
                    setFieldValue(`${addressName}.region`, data?.newValue)
                  }
                  path={BOOKING_PROCESS.GET_REGIONS}
                  pathVariables={{ id: values?.[addressName]?.country?.id }}
                  pathVariablesRequired={['id']}
                  temApi
                />
              </FormControl>
            </Grid>
          </>
        )}
      </StyledGrid>
    </>
  );
};
export default AdditionalAddress;
